// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
import L from 'leaflet';
import type { ViewContext } from '../viewContext';
import { loadGeo } from '../data';
import { RAMP, rampColour } from '../components/charts';
import { byState, MULTI_LABEL, type Group } from '../analysis';
import { OVER, UNDER, positionColour, STATE_NAMES } from '../sectors';
import { escapeHtml, formatCompact, formatNumber } from '../utils/format';
import { glossaryTerm } from '../glossary';

type Metric = 'covered' | 'excess' | 'count' | 'methane';
const METRICS: Record<Metric, { label: string; title: string; sub: string; value: (g: Group) => number; fmt: (v: number) => string }> = {
  covered: { label: 'Covered emissions', title: 'Total covered emissions by state', sub: 'The sum of every Safeguard facility\'s covered emissions in each state. Western Australia and Queensland — gas and coal — dominate.', value: (g) => g.covered, fmt: (v) => `${formatCompact(v)} t` },
  excess: { label: 'Over baseline', title: 'Net tonnes over baseline by state', sub: 'Covered emissions minus baseline, summed. Positive (ember) means the state\'s facilities emitted above their caps overall.', value: (g) => g.excess, fmt: (v) => `${v > 0 ? '+' : ''}${formatCompact(v)} t` },
  count: { label: 'Facilities', title: 'Number of covered facilities by state', sub: 'How many Safeguard-covered facilities operate in each state.', value: (g) => g.count, fmt: (v) => `${formatNumber(v)}` },
  methane: { label: 'Methane share', title: 'Methane share of emissions by state', sub: 'Share of covered emissions that is fugitive methane — highest where coal mining and gas dominate.', value: (g) => g.methaneShare ?? 0, fmt: (v) => `${v.toFixed(1)}%` },
};

const MAINLAND = L.latLngBounds([-43.8, 112.5], [-9.8, 154.2]);

/** Skew-aware stops: 6 quantile cut points over the positive values. */
function quantileStops(values: number[]): number[] {
  const v = values.filter((x) => Number.isFinite(x)).slice().sort((a, b) => a - b);
  if (v.length === 0) return [1, 2, 3, 4, 5, 6];
  const qs = [0.15, 0.3, 0.45, 0.6, 0.78, 0.92];
  return qs.map((q) => {
    const pos = (v.length - 1) * q;
    const lo = Math.floor(pos);
    const hi = Math.ceil(pos);
    return lo === hi ? v[lo] : v[lo] + (v[hi] - v[lo]) * (pos - lo);
  });
}

export function renderMap(root: HTMLElement, ctx: ViewContext): void {
  const { facilities } = ctx.data;
  let metric: Metric = (localStorage.getItem('sg.map.metric') as Metric) || 'covered';
  if (!METRICS[metric]) metric = 'covered';

  const groups = byState(facilities);
  const byCode = new Map(groups.map((g) => [g.key, g]));
  const multi = facilities.filter((f) => f.state === 'MULTI');

  root.innerHTML = `
    <div class="view-head">
      <h1 class="view-title">The map of Australia's biggest emitters</h1>
      <p class="view-sub">
        Every state shaded by its Safeguard-covered ${glossaryTerm('covered', 'emissions')}. Click a state to list its
        facilities; click a facility to open it. ${multi.length} national or cross-state facilities (airlines, pipelines,
        rail) have no single home and are listed separately below.
      </p>
    </div>

    <div class="controls">
      <span class="field-label">Colour by</span>
      <div class="seg" role="group" data-role="metric">
        ${(Object.keys(METRICS) as Metric[]).map((m) => `<button class="seg-btn" data-metric="${m}" aria-pressed="${m === metric}">${METRICS[m].label}</button>`).join('')}
      </div>
    </div>

    <div class="grid-2 wide">
      <div class="panel">
        <div class="panel-title" data-role="title"></div>
        <div class="panel-sub" data-role="sub"></div>
        <div class="map-frame"><div class="map-canvas"></div></div>
        <div class="map-legend" data-role="legend"></div>
      </div>
      <div class="panel" data-role="side">
        <div class="panel-title" data-role="side-title">Select a state</div>
        <div class="panel-sub" data-role="side-sub">Click a state on the map to see its facilities and totals.</div>
        <div data-role="side-body"></div>
      </div>
    </div>
  `;

  const canvas = root.querySelector('.map-canvas') as HTMLElement;
  const titleEl = root.querySelector('[data-role="title"]') as HTMLElement;
  const subEl = root.querySelector('[data-role="sub"]') as HTMLElement;
  const legendEl = root.querySelector('[data-role="legend"]') as HTMLElement;
  const sideTitle = root.querySelector('[data-role="side-title"]') as HTMLElement;
  const sideSub = root.querySelector('[data-role="side-sub"]') as HTMLElement;
  const sideBody = root.querySelector('[data-role="side-body"]') as HTMLElement;

  const map = L.map(canvas, { minZoom: 3, maxZoom: 8, scrollWheelZoom: false, preferCanvas: true });
  map.attributionControl.setPrefix(false);
  L.tileLayer('https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png', { attribution: 'Tiles © CARTO', subdomains: 'abcd', minZoom: 3, maxZoom: 8 }).addTo(map);
  map.fitBounds(MAINLAND, { padding: [6, 6] });

  let stops = quantileStops(groups.map((g) => METRICS[metric].value(g)));
  let layer: L.GeoJSON | null = null;

  const fillFor = (code: string): string => {
    const g = byCode.get(code);
    if (!g) return '#e8ecef';
    const v = METRICS[metric].value(g);
    if (metric === 'excess') {
      // Diverging: ember for over, teal for under, intensity by magnitude.
      const mag = Math.min(1, Math.abs(v) / (Math.max(...groups.map((x) => Math.abs(x.excess))) || 1));
      const base = v >= 0 ? OVER : UNDER;
      return shade(base, 0.25 + mag * 0.75);
    }
    return rampColour(v, stops);
  };

  const styleFor = (code: string) => ({ fillColor: fillFor(code), fillOpacity: 0.85, color: '#ffffff', weight: 0.8 });

  const renderLegend = () => {
    const cfg = METRICS[metric];
    if (metric === 'excess') {
      legendEl.innerHTML = `<span class="field-label">net vs baseline</span>
        <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${UNDER}"></span>under</span>
        <span class="legend-item" style="cursor:default"><span class="legend-swatch" style="background:${OVER}"></span>over</span>
        <span class="legend-item" style="cursor:default;margin-left:auto"><span class="legend-swatch" style="background:#e8ecef"></span>No covered facilities</span>`;
      return;
    }
    legendEl.innerHTML = `
      <span class="field-label">${escapeHtml(cfg.label.toLowerCase())}</span>
      <span class="scale-bar">${RAMP.map((c) => `<span class="scale-swatch" style="background:${c}"></span>`).join('')}</span>
      <span style="font-family:var(--font-mono);font-size:var(--font-size-xs)">low → high</span>
      <span class="legend-item" style="cursor:default;margin-left:auto"><span class="legend-swatch" style="background:#e8ecef"></span>No covered facilities</span>`;
  };

  const paint = () => {
    const cfg = METRICS[metric];
    titleEl.textContent = cfg.title;
    subEl.textContent = cfg.sub;
    stops = quantileStops(groups.map((g) => cfg.value(g)));
    renderLegend();
    if (layer) layer.setStyle((f: any) => styleFor(f.properties.code));
    root.querySelectorAll('[data-metric]').forEach((b) => b.setAttribute('aria-pressed', String(b.getAttribute('data-metric') === metric)));
  };

  const showState = (code: string) => {
    const g = byCode.get(code);
    sideTitle.textContent = STATE_NAMES[code] ?? code;
    if (!g) {
      sideSub.textContent = 'No Safeguard-covered facilities in this state.';
      sideBody.innerHTML = '';
      return;
    }
    sideSub.innerHTML = `${formatCompact(g.covered)} tCO₂-e covered · ${g.count} facilities · ${g.nOver} over baseline · methane ${(g.methaneShare ?? 0).toFixed(0)}%`;
    sideBody.innerHTML = `
      <div class="table-scroll" style="max-height:440px;overflow-y:auto">
        <table class="data">
          <thead><tr><th>Facility</th><th class="t-num">Covered</th><th class="t-num">vs base</th></tr></thead>
          <tbody>
            ${g.facilities
              .slice(0, 40)
              .map(
                (f) => `<tr data-slug="${escapeHtml(f.slug)}">
                  <td class="loc-cell">${escapeHtml(f.name)}<br><span class="muted" style="font-size:var(--font-size-xs)">${escapeHtml(f.company)}</span></td>
                  <td class="t-num">${formatCompact(f.covered)}</td>
                  <td class="t-num" style="color:${positionColour(f.excess)}">${f.excess > 0 ? '+' : ''}${formatCompact(f.excess)}</td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>`;
  };

  root.querySelector('[data-role="metric"]')?.addEventListener('click', (e) => {
    const btn = (e.target as Element).closest('[data-metric]');
    if (!btn) return;
    metric = btn.getAttribute('data-metric') as Metric;
    localStorage.setItem('sg.map.metric', metric);
    paint();
  });

  sideBody.addEventListener('click', (e) => {
    const tr = (e.target as Element).closest('[data-slug]');
    if (tr) ctx.openFacility(tr.getAttribute('data-slug') as string);
  });

  loadGeo()
    .then((geo) => {
      layer = L.geoJSON(geo, {
        attribution: 'Boundaries: ABS ASGS 2021 (CC BY 4.0) · Data: Clean Energy Regulator',
        style: (f: any) => styleFor(f.properties.code),
        onEachFeature: (f: any, lyr: L.Layer) => {
          const code = f.properties.code as string;
          const g = byCode.get(code);
          const tip = g
            ? `<strong>${escapeHtml(STATE_NAMES[code] ?? code)}</strong><br>${formatCompact(g.covered)} tCO₂-e · ${g.count} facilities<br><span style="color:#475569">${g.nOver} over baseline</span>`
            : `<strong>${escapeHtml(STATE_NAMES[code] ?? code)}</strong><br><span style="color:#475569">No covered facilities</span>`;
          lyr.bindTooltip(tip, { sticky: true, className: 'map-tip' });
          lyr.on({
            mouseover: () => (lyr as L.Path).setStyle({ weight: 2, color: '#0f172a' }),
            mouseout: () => layer?.resetStyle(lyr as L.Path),
            click: () => showState(code),
          });
        },
      }).addTo(map);
      requestAnimationFrame(() => {
        map.invalidateSize();
        map.fitBounds(MAINLAND, { padding: [6, 6] });
      });
      paint();
      // Default the side panel to the biggest state.
      if (groups[0]) showState(groups[0].key);
    })
    .catch(() => {
      canvas.innerHTML = '<div class="error-box" style="margin:var(--space-lg)">Could not load the state boundaries.</div>';
    });

  paint();

  // National / multi-state facilities.
  if (multi.length) {
    const panel = document.createElement('div');
    panel.className = 'panel';
    panel.style.marginTop = 'var(--space-lg)';
    const mCovered = multi.reduce((s, f) => s + f.covered, 0);
    panel.innerHTML = `
      <div class="panel-title">${escapeHtml(MULTI_LABEL)} facilities</div>
      <div class="panel-sub">${multi.length} facilities with no single state — airlines, cross-state pipelines and rail — releasing ${formatCompact(mCovered)} tCO₂-e. Click to open.</div>
      <div class="table-scroll">
        <table class="data">
          <thead><tr><th>Facility</th><th>Company</th><th class="t-num">Covered</th><th class="t-num">vs base</th></tr></thead>
          <tbody>
            ${multi
              .sort((a, b) => b.covered - a.covered)
              .map(
                (f) => `<tr data-slug="${escapeHtml(f.slug)}">
                  <td class="loc-cell">${escapeHtml(f.name)}</td>
                  <td class="loc-cell">${escapeHtml(f.company)}</td>
                  <td class="t-num">${formatCompact(f.covered)}</td>
                  <td class="t-num" style="color:${positionColour(f.excess)}">${f.excess > 0 ? '+' : ''}${formatCompact(f.excess)}</td>
                </tr>`,
              )
              .join('')}
          </tbody>
        </table>
      </div>`;
    panel.addEventListener('click', (e) => {
      const tr = (e.target as Element).closest('[data-slug]');
      if (tr) ctx.openFacility(tr.getAttribute('data-slug') as string);
    });
    root.appendChild(panel);
  }
}

/** Lighten/darken a hex toward white by fraction (0 = white, 1 = full colour). */
function shade(hex: string, frac: number): string {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const mix = (v: number) => Math.round(255 - (255 - v) * frac);
  return `rgb(${mix(r)},${mix(g)},${mix(b)})`;
}
